import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import type { GridState } from "ag-grid-community"
import {
  CaretDown,
  FloppyDisk,
  PencilSimple,
  Plus,
  Stack,
  TrashSimple,
  User,
  UsersThree,
} from "@phosphor-icons/react"
import { useAuth0 } from "@auth0/auth0-react"

import {
  Button,
  Checkbox,
  IconButton,
  Input,
  Textarea,
} from "~/components/design-system/components"
import {
  DialogRoot,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { Label } from "~/components/reusable/Label"
import type { SavedTableView } from "~/server/api/routers/tableView"
import { canModifySavedView, resolveViewScope } from "~/server/api/utils/viewAccess"
import { useUser } from "~/providers/userProvider"
import { api } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"
import { cn } from "~/utils/helpers"
import type { Filter } from "~/utils/filter"

/** What a saved view captures on a table. On Replenishment the line pills (all /
 * below reorder point / constrained) are quick filters and deliberately not part of a view. */
export type SavedViewSnapshot = {
  filters: Filter[]
  groupBy: string
  gridState: GridState | undefined
}

type DialogState = { mode: "create" } | { mode: "edit"; view: SavedTableView } | null

export function SavedViewSwitcher({
  page,
  activeView,
  onApply,
  onClear,
  getSnapshot,
}: {
  /** The table's saved-view page id. */
  page: string
  activeView: SavedTableView | null
  onApply: (view: SavedTableView) => void
  onClear: () => void
  getSnapshot: () => SavedViewSnapshot
}) {
  const { user } = useAuth0()
  const { permissions } = useUser()
  const utils = api.useUtils()

  const [popoverOpen, setPopoverOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [dialog, setDialog] = useState<DialogState>(null)

  const { data: views } = api.savedTableView.get.useQuery({
    pages: [page],
    isHakio: permissions.isStaff,
  })

  const upsertView = api.savedTableView.upsert.useMutation({
    onSuccess: (view) => {
      void utils.savedTableView.get.invalidate()
      toast.success(`The view ${view.name} was saved.`)
    },
    onError: showErrorToast,
  })
  const deleteView = api.savedTableView.delete.useMutation({
    onSuccess: () => {
      void utils.savedTableView.get.invalidate()
    },
    onError: showErrorToast,
  })

  const sections = useMemo(() => {
    const query = search.trim().toLowerCase()
    const withType = (views ?? []).map((view) => ({
      ...view,
      type: resolveViewScope(view, user?.sub),
    }))
    const matches = (view: SavedTableView) =>
      query === "" ||
      view.name.toLowerCase().includes(query) ||
      (view.description ?? "").toLowerCase().includes(query)

    const personal = withType.filter((view) => view.type === "personal" && matches(view))
    const shared = withType.filter((view) => view.type === "shared" && matches(view))
    const other = withType.filter((view) => view.type === "other" && matches(view))

    return [
      { key: "personal", label: "My views", views: personal },
      { key: "shared", label: "Shared", views: shared },
      // Other users' personal views, staff-only via the get query.
      { key: "other", label: "Other users", views: other },
    ].filter((section) => section.views.length > 0)
  }, [views, search, user?.sub])

  const applyView = (view: SavedTableView) => {
    onApply(view)
    setPopoverOpen(false)
  }

  const saveSnapshotToActiveView = async () => {
    if (!activeView?.id) return
    const snapshot = getSnapshot()
    const saved = await upsertView.mutateAsync({
      id: activeView.id,
      name: activeView.name,
      description: activeView.description,
      userId: activeView.userId,
      filters: snapshot.filters,
      page,
      aggregationMethods: [snapshot.groupBy],
      warehouseId: null,
      channel: null,
      gridState: snapshot.gridState,
    })
    onApply(saved)
  }

  const activeIsModifiable = !!activeView && canModifySavedView(permissions, activeView, user?.sub)

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="neutral">
            <Stack size={14} />
            {activeView ? activeView.name : "Views"}
            <CaretDown size={12} className="text-fg-muted" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-96 p-0">
          <div className="border-b border-base p-2">
            <Input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search views"
              className="h-8"
            />
          </div>
          <div className="max-h-80 overflow-y-auto p-1">
            {sections.length === 0 && (
              <div className="paragraph-sm px-2 py-4 text-center text-fg-muted">
                {search ? "No views match your search" : "No saved views yet"}
              </div>
            )}
            {sections.map((section) => (
              <div key={section.key} className="py-1">
                <div className="label-xs flex items-center gap-1 px-2 py-1 text-fg-muted">
                  {section.key === "personal" ? <User size={11} /> : <UsersThree size={11} />}
                  {section.label}
                </div>
                {section.views.map((view) => {
                  const modifiable = canModifySavedView(permissions, view, user?.sub)
                  return (
                    <div
                      key={view.id}
                      className={cn(
                        "group flex w-full cursor-pointer items-center gap-1 rounded px-2 py-1.5 hover:bg-component-hover",
                        activeView?.id === view.id && "bg-component-pressed",
                      )}
                      onClick={() => applyView(view)}
                    >
                      <span
                        className="paragraph-sm min-w-0 flex-1 truncate"
                        title={view.description ?? undefined}
                      >
                        {view.name}
                      </span>
                      {modifiable && (
                        <span
                          className="hidden shrink-0 items-center gap-0.5 group-hover:flex"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <IconButton
                            size="xs"
                            variant="transparent"
                            aria-label="Edit view"
                            onClick={() => setDialog({ mode: "edit", view })}
                          >
                            <PencilSimple size={13} />
                          </IconButton>
                          <ConfirmationDialog
                            title="Delete view"
                            description={`Delete the view ${view.name}? This cannot be undone.`}
                            confirmText="Delete"
                            variant="danger"
                            onConfirm={() => {
                              if (view.id === undefined) return
                              deleteView.mutate(
                                { id: view.id },
                                { onSuccess: () => activeView?.id === view.id && onClear() },
                              )
                            }}
                            trigger={
                              <IconButton size="xs" variant="transparent" aria-label="Delete view">
                                <TrashSimple size={13} />
                              </IconButton>
                            }
                          />
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1 border-t border-base p-2">
            <Button
              size="sm"
              variant="transparent"
              onClick={() => {
                setDialog({ mode: "create" })
                setPopoverOpen(false)
              }}
            >
              <Plus size={13} />
              Save current as view
            </Button>
            <div className="flex-1" />
            {activeView && activeIsModifiable && (
              <Button
                size="sm"
                variant="transparent"
                loading={upsertView.isPending}
                onClick={() => void saveSnapshotToActiveView()}
              >
                <FloppyDisk size={13} />
                Save changes
              </Button>
            )}
            {activeView && (
              <Button
                size="sm"
                variant="transparent"
                onClick={() => {
                  onClear()
                  setPopoverOpen(false)
                }}
              >
                Clear view
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {dialog && (
        <ManageSavedViewDialog
          page={page}
          dialog={dialog}
          onClose={() => setDialog(null)}
          getSnapshot={getSnapshot}
          onSaved={(view) => {
            setDialog(null)
            onApply(view)
          }}
        />
      )}
    </>
  )
}

function ManageSavedViewDialog({
  page,
  dialog,
  onClose,
  getSnapshot,
  onSaved,
}: {
  page: string
  dialog: NonNullable<DialogState>
  onClose: () => void
  getSnapshot: () => SavedViewSnapshot
  onSaved: (view: SavedTableView) => void
}) {
  const { user } = useAuth0()
  const utils = api.useUtils()

  const editedView = dialog.mode === "edit" ? dialog.view : null
  const [name, setName] = useState(editedView?.name ?? "")
  const [description, setDescription] = useState(editedView?.description ?? "")
  const [shared, setShared] = useState(editedView ? editedView.userId === null : false)

  const upsertView = api.savedTableView.upsert.useMutation({
    onSuccess: (view) => {
      void utils.savedTableView.get.invalidate()
      toast.success(`The view ${view.name} was saved.`)
      onSaved(view)
    },
    onError: showErrorToast,
  })

  const handleSave = () => {
    const base = {
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
      userId: shared ? null : (user?.sub ?? null),
      page,
      warehouseId: null,
      channel: null,
    }
    if (editedView) {
      // Only identity fields change here; the view keeps its stored layout and filters.
      upsertView.mutate({
        ...base,
        id: editedView.id,
        filters: editedView.filters,
        aggregationMethods: editedView.aggregationMethods,
        gridState: editedView.gridState,
      })
      return
    }
    const snapshot = getSnapshot()
    upsertView.mutate({
      ...base,
      filters: snapshot.filters,
      aggregationMethods: [snapshot.groupBy],
      gridState: snapshot.gridState,
    })
  }

  return (
    <DialogRoot open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editedView ? "Edit view" : "Save view"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="repl-view-name">Name</Label>
            <Input
              id="repl-view-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Store replenishment focus"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="repl-view-description">Description</Label>
            <Textarea
              id="repl-view-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What this view is for"
              small
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="repl-view-shared"
              checked={shared}
              onCheckedChange={(checked) => setShared(checked === true)}
              requires="edit"
              allowedTitle="Shared views are visible to the whole company."
            />
            <Label htmlFor="repl-view-shared">Share with company</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={name.trim() === ""} loading={upsertView.isPending}>
            {editedView ? "Save changes" : "Save view"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
